import React, { useState, useEffect } from 'react';
import {
  Building2,
  Shield,
  Voicemail,
  Mic,
  Network,
  Save,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { settingsAPI } from '../services/api';

const Settings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await settingsAPI.get();
      setSettings(response.data);
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await settingsAPI.update(settings);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Error saving settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const updateGeneral = (field, value) => {
    setSettings({
      ...settings,
      general: { ...settings.general, [field]: value }
    });
  };

  const updateSecurity = (field, value) => {
    setSettings({
      ...settings,
      security: { ...settings.security, [field]: value }
    });
  };

  const updateVoicemail = (field, value) => {
    setSettings({
      ...settings,
      voicemail: { ...settings.voicemail, [field]: value }
    });
  };

  const updateRecording = (field, value) => {
    setSettings({
      ...settings,
      recording: { ...settings.recording, [field]: value }
    });
  };

  const updateNetwork = (field, value) => {
    setSettings({
      ...settings,
      network: { ...settings.network, [field]: value }
    });
  };

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400 mt-1">Configure system settings and preferences</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          className={saved ? 'bg-green-600 hover:bg-green-700' : 'bg-blue-600 hover:bg-blue-700'}
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
          ) : saved ? (
            <><RefreshCw className="w-4 h-4 mr-2" /> Saved!</>
          ) : (
            <><Save className="w-4 h-4 mr-2" /> Save Changes</>
          )}
        </Button>
      </div>

      <Tabs defaultValue="general" className="w-full">
        <TabsList className="bg-[#1e1e2d] border border-[#2a2a3d] flex-wrap h-auto p-1">
          <TabsTrigger value="general" className="data-[state=active]:bg-blue-600">
            <Building2 className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-blue-600">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="voicemail" className="data-[state=active]:bg-blue-600">
            <Voicemail className="w-4 h-4 mr-2" />
            Voicemail
          </TabsTrigger>
          <TabsTrigger value="recording" className="data-[state=active]:bg-blue-600">
            <Mic className="w-4 h-4 mr-2" />
            Recording
          </TabsTrigger>
          <TabsTrigger value="network" className="data-[state=active]:bg-blue-600">
            <Network className="w-4 h-4 mr-2" />
            Network
          </TabsTrigger>
        </TabsList>

        {/* General Tab */}
        <TabsContent value="general" className="mt-6">
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardHeader>
              <CardTitle className="text-white">General Settings</CardTitle>
              <CardDescription className="text-gray-400">
                Configure basic system information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-400">System Name</Label>
                  <Input
                    value={settings.general?.system_name || ''}
                    onChange={(e) => updateGeneral('system_name', e.target.value)}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Company Name</Label>
                  <Input
                    value={settings.general?.company_name || ''}
                    onChange={(e) => updateGeneral('company_name', e.target.value)}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Timezone</Label>
                  <Select
                    value={settings.general?.timezone || ''}
                    onValueChange={(value) => updateGeneral('timezone', value)}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="America/New_York">America/New_York (EST)</SelectItem>
                      <SelectItem value="America/Chicago">America/Chicago (CST)</SelectItem>
                      <SelectItem value="America/Denver">America/Denver (MST)</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los_Angeles (PST)</SelectItem>
                      <SelectItem value="Europe/London">Europe/London (GMT)</SelectItem>
                      <SelectItem value="Europe/Paris">Europe/Paris (CET)</SelectItem>
                      <SelectItem value="Asia/Tokyo">Asia/Tokyo (JST)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Language</Label>
                  <Select
                    value={settings.general?.language || ''}
                    onValueChange={(value) => updateGeneral('language', value)}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="en-US">English (US)</SelectItem>
                      <SelectItem value="en-GB">English (UK)</SelectItem>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="fr">French</SelectItem>
                      <SelectItem value="de">German</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Date Format</Label>
                  <Select
                    value={settings.general?.date_format || ''}
                    onValueChange={(value) => updateGeneral('date_format', value)}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="MM/DD/YYYY">MM/DD/YYYY</SelectItem>
                      <SelectItem value="DD/MM/YYYY">DD/MM/YYYY</SelectItem>
                      <SelectItem value="YYYY-MM-DD">YYYY-MM-DD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Time Format</Label>
                  <Select
                    value={settings.general?.time_format || ''}
                    onValueChange={(value) => updateGeneral('time_format', value)}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="12h">12 Hour</SelectItem>
                      <SelectItem value="24h">24 Hour</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-6">
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardHeader>
              <CardTitle className="text-white">Security Settings</CardTitle>
              <CardDescription className="text-gray-400">
                Configure authentication and access control
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                <div>
                  <p className="text-white font-medium">Two-Factor Authentication</p>
                  <p className="text-sm text-gray-400">Require 2FA for all admin accounts</p>
                </div>
                <Switch
                  checked={settings.security?.two_factor_auth || false}
                  onCheckedChange={(checked) => updateSecurity('two_factor_auth', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                <div>
                  <p className="text-white font-medium">Single Sign-On (SSO)</p>
                  <p className="text-sm text-gray-400">Enable SSO integration</p>
                </div>
                <Switch
                  checked={settings.security?.sso_enabled || false}
                  onCheckedChange={(checked) => updateSecurity('sso_enabled', checked)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-400">Failed Login Lockout (attempts)</Label>
                  <Input
                    type="number"
                    value={settings.security?.failed_login_lockout || 5}
                    onChange={(e) => updateSecurity('failed_login_lockout', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Session Timeout (minutes)</Label>
                  <Input
                    type="number"
                    value={settings.security?.session_timeout || 30}
                    onChange={(e) => updateSecurity('session_timeout', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-gray-400">IP Whitelist (comma separated)</Label>
                <Input
                  value={(settings.security?.ip_whitelist || []).join(', ')}
                  onChange={(e) => updateSecurity('ip_whitelist', e.target.value.split(',').map(s => s.trim()))}
                  placeholder="192.168.1.0/24, 10.0.0.0/8"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Voicemail Tab */}
        <TabsContent value="voicemail" className="mt-6">
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardHeader>
              <CardTitle className="text-white">Voicemail Settings</CardTitle>
              <CardDescription className="text-gray-400">
                Configure voicemail behavior and notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-400">Max Message Length (seconds)</Label>
                  <Input
                    type="number"
                    value={settings.voicemail?.max_message_length || 180}
                    onChange={(e) => updateVoicemail('max_message_length', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Max Messages per Mailbox</Label>
                  <Input
                    type="number"
                    value={settings.voicemail?.max_messages || 50}
                    onChange={(e) => updateVoicemail('max_messages', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                <div>
                  <p className="text-white font-medium">Email Notification</p>
                  <p className="text-sm text-gray-400">Send email when new voicemail received</p>
                </div>
                <Switch
                  checked={settings.voicemail?.email_notification || false}
                  onCheckedChange={(checked) => updateVoicemail('email_notification', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                <div>
                  <p className="text-white font-medium">Attach Recording to Email</p>
                  <p className="text-sm text-gray-400">Include audio file in notification email</p>
                </div>
                <Switch
                  checked={settings.voicemail?.attach_recording || false}
                  onCheckedChange={(checked) => updateVoicemail('attach_recording', checked)}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                <div>
                  <p className="text-white font-medium">Delete After Email</p>
                  <p className="text-sm text-gray-400">Delete voicemail after sending email</p>
                </div>
                <Switch
                  checked={settings.voicemail?.delete_after_email || false}
                  onCheckedChange={(checked) => updateVoicemail('delete_after_email', checked)}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recording Tab */}
        <TabsContent value="recording" className="mt-6">
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardHeader>
              <CardTitle className="text-white">Call Recording Settings</CardTitle>
              <CardDescription className="text-gray-400">
                Configure call recording options and storage
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-[#1e1e2d] rounded-lg">
                <div>
                  <p className="text-white font-medium">Enable Call Recording</p>
                  <p className="text-sm text-gray-400">Record all calls by default</p>
                </div>
                <Switch
                  checked={settings.recording?.enabled || false}
                  onCheckedChange={(checked) => updateRecording('enabled', checked)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-400">Recording Format</Label>
                  <Select
                    value={settings.recording?.format || 'wav'}
                    onValueChange={(value) => updateRecording('format', value)}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="wav">WAV (Uncompressed)</SelectItem>
                      <SelectItem value="mp3">MP3</SelectItem>
                      <SelectItem value="ogg">OGG</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Storage Location</Label>
                  <Select
                    value={settings.recording?.storage || 'local'}
                    onValueChange={(value) => updateRecording('storage', value)}
                  >
                    <SelectTrigger className="bg-[#1e1e2d] border-[#2a2a3d] text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1e1e2d] border-[#2a2a3d]">
                      <SelectItem value="local">Local Storage</SelectItem>
                      <SelectItem value="s3">Amazon S3</SelectItem>
                      <SelectItem value="gcs">Google Cloud Storage</SelectItem>
                      <SelectItem value="azure">Azure Blob Storage</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">Retention Period (days)</Label>
                  <Input
                    type="number"
                    value={settings.recording?.retention_days || 90}
                    onChange={(e) => updateRecording('retention_days', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Network Tab */}
        <TabsContent value="network" className="mt-6">
          <Card className="bg-[#12121a] border-[#1e1e2d]">
            <CardHeader>
              <CardTitle className="text-white">Network Settings</CardTitle>
              <CardDescription className="text-gray-400">
                Configure SIP and RTP network settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-400">SIP Port (UDP)</Label>
                  <Input
                    type="number"
                    value={settings.network?.sip_port || 5060}
                    onChange={(e) => updateNetwork('sip_port', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">SIP TLS Port</Label>
                  <Input
                    type="number"
                    value={settings.network?.tls_port || 5061}
                    onChange={(e) => updateNetwork('tls_port', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">RTP Port Range Start</Label>
                  <Input
                    type="number"
                    value={settings.network?.rtp_port_start || 10000}
                    onChange={(e) => updateNetwork('rtp_port_start', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-gray-400">RTP Port Range End</Label>
                  <Input
                    type="number"
                    value={settings.network?.rtp_port_end || 20000}
                    onChange={(e) => updateNetwork('rtp_port_end', parseInt(e.target.value))}
                    className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-400">STUN Server</Label>
                <Input
                  value={settings.network?.stun_server || ''}
                  onChange={(e) => updateNetwork('stun_server', e.target.value)}
                  placeholder="stun.l.google.com:19302"
                  className="bg-[#1e1e2d] border-[#2a2a3d] text-white"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
